import { z } from "zod";

/// URL search params for the team leave timeline.
///
/// Every one of them lives in the URL rather than in component state (H1): a
/// manager checking whether a request clashes with somebody else's leave sends
/// the link, and a screen that reset on reload would send them a different page
/// from the one being talked about.
///
/// `weeks` is here specifically because the calendar route refuses outright
/// past a thousand pieces of leave and offers no cursor to page around it. The
/// only recovery this screen has is to ask for less, so the size of the ask has
/// to be a value the screen can change, and one that survives the reload.
export const leaveTeamRouteSearchSchema = z
  .object({
    /// "YYYY-MM-DD". Any day in the week the timeline should open on; the page
    /// snaps it to that week's Monday. Defaults to this week.
    from: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    /// How many weeks are drawn. Four by default: long enough to plan a month
    /// of cover, short enough that twenty eight columns still fit a laptop.
    weeks: z.coerce.number().int().min(1).max(4).optional(),
    /// The three filters, in the URL for the same reason the window is: the
    /// link a manager sends is the screen they were looking at, filters and
    /// all. All three narrow what is already in the browser, so none of them
    /// changes the request.
    ///
    /// Bounded because they arrive from the address bar: a name query is
    /// matched as a substring, and an id is compared against ids the response
    /// carried, so neither can do anything with the extra length except sit in
    /// the URL.
    person: z.string().max(80).optional(),
    leaveTypeId: z.string().max(64).optional(),
    /// Only these two ever reach the calendar; nothing else holds dates.
    status: z.enum(["approved", "pending"]).optional(),
    /// Which job the page is doing: reading who is away, or deciding what is
    /// waiting. A tab and not a second route, matching attendance: the two are
    /// the same person's jobs on the same people, and splitting them made a
    /// manager's requests a page while an employee's were a tab.
    tab: z.enum(["away", "decide"]).optional(),
    /// Which of the two views is on screen. In the URL for the same reason
    /// everything else here is: the link a manager sends should open on the
    /// view they were looking at, and a reader who prefers rows should not
    /// have to re-pick them on every reload.
    view: z.enum(["calendar", "table"]).optional(),
    applicationId: z.string().min(1).max(128).optional(),
    returnTo: z
      .string()
      .max(2048)
      .regex(/^\/(?!\/)/)
      .optional(),
  })
  .strict();

export type LeaveTeamRouteSearch = z.infer<typeof leaveTeamRouteSearchSchema>;
